import { ReactElement, useEffect, useMemo, } from "react";
import { useQuery } from "react-query";
import moment from "moment";
import { useLocalization } from "@progress/kendo-react-intl";
import { EventFilter } from "../../Events/Utilities/EventsApiService";
import { RequirementAnalysis } from "../../../Models/Requirement/RequirementAnalysis";
import requirementApiService from "../../Requirements/Utilities/RequirementApiService";
import { ConsumableReadDto } from "../../../Models/Consumable/ConsumableReadDto";
import { ReactQueryKeys } from "../../../Constants/ClientRoutingConstants";
import consumablesApiService from "../../Consumables/Utilities/ConsumablesApiService";
import { rruleSummaryToText } from "../Utilities/ReportUtilities";
import LoadingSpinner from "../Components/ProcessingSpinner";
import NoRequirementPage from "../../SiteExplorer/Pages/NoRequirementPage";
import { enMessages } from "../../../messages/en-US";
import siteExplorerApiService from "../../SiteExplorer/Utilities/SiteExplorerApiService";
import EndpointStatusChart from "../../SiteExplorer/Pages/Endpoints/EndpointStatusChart";
import { ChartToolbar } from "../../SiteExplorer/Utilities/ChartToolbar";

interface Props {
  eventFilter: EventFilter
  endpointId: string
}

export default function EndpointPvADialogPage(props: Props): ReactElement {
  const localizationService = useLocalization();
  
  const requirementsQuery = useQuery<RequirementAnalysis>('endpointReportRequirement', async () => {
    return requirementApiService.getAnalysisRequirement(props.endpointId, props.eventFilter);
  });

  const consumablesQuery = useQuery<Array<ConsumableReadDto>>(ReactQueryKeys.AllConsumablesQuery, async () => {
    return consumablesApiService.getAllConsumables();
  });

  const requirementResult = useMemo(() => {
    const consumableIdToName: { [id: string]: string } = {};
    consumablesQuery.data?.forEach((consumable) => {
      consumableIdToName[consumable.id] = consumable.name;
    });
    //TODO: This is a hack to get around the fact that the API returns a 200 with an empty array when there is no requirement.
    if(requirementsQuery.data?.requirementName !== "") {

    return{
        timestamps: requirementsQuery.data?.results.length === 0 ? requirementsQuery.data?.results : requirementsQuery.data?.results?.map(x => moment(x.start).unix()),
        volumeDispensedData: requirementsQuery.data?.results.length === 0 ? requirementsQuery.data?.results : requirementsQuery.data?.results?.map(item => item.volumeDispensed),
        volumeRequirement: requirementsQuery.data?.requirementDetails?.rightAmountDetails?.volumeRequirement,
        thresholds: requirementsQuery.data?.requirementDetails?.rightAmountDetails?.thresholds,
        summary: requirementsQuery.data?.requirementDetails?.rightTimeDetails?.rRule === undefined || requirementsQuery.data?.requirementDetails?.rightTimeDetails?.rRule === '' ? "No Requirement" : rruleSummaryToText(requirementsQuery.data?.requirementDetails?.rightTimeDetails?.rRule),
        consumableName: requirementsQuery.data?.requirementDetails.rightGreaseDetails?.consumableId === undefined ? '' : consumableIdToName[requirementsQuery.data?.requirementDetails.rightGreaseDetails?.consumableId],
        startDate: moment(requirementsQuery.data?.requirementDetails?.rightTimeDetails?.startDate).unix() ,
        endDate: moment(requirementsQuery.data?.requirementDetails?.rightTimeDetails?.endDate).unix() === moment(new Date('0001-01-01T00:00:00Z')).unix() ? moment(props.eventFilter.endDate).unix() : moment(requirementsQuery.data?.requirementDetails?.rightTimeDetails?.endDate).unix(),
        unplannedEventsTimestamps: requirementsQuery.data?.unplannedEvents.map(x => moment(x.timestamp).unix()),
        unplannedEventsVolumes: requirementsQuery.data?.unplannedEvents.map(x => x.volume),
        complianceWindowInMinutes: requirementsQuery.data?.requirementDetails?.rightTimeDetails?.complianceWindowInMinutes,
    }} else {
      return {
        timestamps: [],
        volumeDispensedData: [],
        volumeRequirement: 0,
        thresholds: [],
        summary: '',
        consumableName: '',
        startDate: moment().unix(),
        endDate: moment().unix(),
        unplannedEventsTimestamps: [],
        unplannedEventsVolumes: [],
        complianceWindowInMinutes: 0,
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[requirementsQuery.data]);


  useEffect(() => {
    requirementsQuery.refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.endpointId, props.eventFilter]);

  const notificationsQuery = useQuery<any>(
    ["notificationsStatus", props.endpointId, props.eventFilter],
    async () => {
      return siteExplorerApiService.getEndpointNotifications(props.endpointId, props.eventFilter, 1);
    }
  );

  const statusQuery = useQuery<any>(
    ["statusChart", props.endpointId, props.eventFilter],
    async () => {
      return siteExplorerApiService.getEndpointStatus(props.endpointId, props.eventFilter, 0);
    }
  );

  

  const chartData = useMemo(() => {
    const lastSyncUtcUnix = requirementsQuery.data?.lastSyncUtc ? moment(requirementsQuery.data.lastSyncUtc).unix() : 0;
    const lastSeen = requirementsQuery.data?.lastSyncUtc === undefined ? localizationService.toLanguageString('custom.noEndpointDeviceAssigned', enMessages.custom.noEndpointDeviceAssigned) : lastSyncUtcUnix < 0 ? localizationService.toLanguageString('custom.noEventsToDetermine', enMessages.custom.noEventsToDetermine) : moment.unix(lastSyncUtcUnix).format("DD/MM/YYYY @ HH:mm:ss");

    return {
      statusDataChart: {dateFilter: props.eventFilter, timestamps: requirementResult.timestamps, statusData:statusQuery.data,  alerts:notificationsQuery.data, },
      
              complianceDataChart: {dateFilter: props.eventFilter, strokeThickness: 1 , requirements:requirementResult,  lastSeen:moment(requirementsQuery.data?.lastSeen) ,lastSeenFormatted: lastSeen, lastSyncUtc: moment(requirementsQuery.data?.lastSyncUtc).unix(), lastHeartbeatUtc:  moment(requirementsQuery.data?.lastHeartbeatUtc).unix(), complianceWindowInMinutes: requirementResult.complianceWindowInMinutes},

    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notificationsQuery.data, statusQuery.data,requirementsQuery.data]);
  

  if (requirementsQuery.isLoading || requirementsQuery.isFetching || consumablesQuery.isLoading || consumablesQuery.isFetching || notificationsQuery.isLoading || notificationsQuery.isFetching || statusQuery.isLoading || statusQuery.isFetching) {
    return <div className="flex items-center justify-center h-full w-full">
      <LoadingSpinner />
    </div>;
  } else {
    if (requirementsQuery.isError || requirementsQuery.data?.requirementName === "" || notificationsQuery.isError || statusQuery.isError || consumablesQuery.isError) {
      return  <div className="text-center"><NoRequirementPage /></div>;
    } else { 
  return (
    <div>
      <div className="mt-2">
        <ChartToolbar 
          title={localizationService.toLanguageString('custom.events', enMessages.custom.events)}
          lastSeen={chartData.complianceDataChart.lastSeenFormatted}
          showAutoRange={true} 
          showAggregatorOptions={false} 
          showChartOptions={true}
          showThresholds={true} 
          showSingleMultipleChart={false}
        /> 
      </div>
      
      <EndpointStatusChart chartType="compliance" statusChartData={chartData.statusDataChart} complianceDataChart={chartData.complianceDataChart} isSimpleChart={false}/>
      
    </div>
  )
}}}